/**
 * Utility module for handling media-related operations in product schemas.
 * @module MediaUtils
 */

import type { ProductSchema } from '#build/armani/product-schema'

/**
 * Checks if a product has images or assets.
 *
 * @param {ProductSchema} item - The product item to check for media.
 * @returns {ProductSchema['images'] | ProductSchema['assets'] | undefined} Images or assets of the product.
 *
 * @description
 * Retrieves the images or assets of a product.
 * Intended to be used during development to check media availability.
 *
 * @example
 * // Returns product images or assets if they exist
 * checkMedia(productItem)
 *
 * @deprecated This method is marked for potential removal in future versions.
 */
export const checkMedia = (item: ProductSchema) => {
  // process.dev
  // console.warn(
  //   'Deprecation notice: you are still checking for images || assets inside algolia. Remove this check when unnecessary'
  // )
  return item?.images || item?.assets
}

/**
 * Retrieves the first media item based on priority.
 *
 * @param {ProductSchema['images'] | ProductSchema['assets']} medias - Array of media items.
 * @returns {string} URL of the first media item with the lowest priority.
 *
 * @description
 * Sorts media items by their priority in ascending order and returns the URL of the first item.
 * If no media is found, returns an empty string.
 *
 * @example
 * // Returns URL of the first media item
 * getFirstMediaByPriority(productImages)
 *
 * @example
 * // Returns empty string if no media exists
 * getFirstMediaByPriority(null)
 */
export const getFirstMediaByPriority = (
  medias: ProductSchema['images'] | ProductSchema['assets']
) => {
  if (!medias) return ''
  // Sort the medias array based on their priority values in ascending order
  medias.sort((a, b) => (a.priority || 0) - (b.priority || 0))

  // Return the URL of the first media item
  return medias.length > 0
    ? ((medias[0] as { URL?: string }).URL ??
        (medias[0] as { ImageURL?: string }).ImageURL)
    : ''
}
